/**
 * Quotes Cache Service
 * Manages caching and validation of real-time quote data
 * Provides smooth, consistent pricing data for positions
 */

import type { QuoteData } from "@/services/market-data-streamer";
import { symbolMappingService } from "./symbol-mapping-service";
import { quotesDatabaseService } from "./quotes-database-service";
import {
  deriveSymbolVariants,
  buildAliasKeys,
  removeLegacyAliases,
} from "./symbol-alias-utils";

export interface CachedQuote {
  symbol: string;
  streamerSymbol: string;
  bidPrice: number | null;
  askPrice: number | null;
  bidSize: number | null;
  askSize: number | null;
  lastPrice: number | null;
  midPrice: number | null;
  spread: number | null;
  spreadPercentage: number | null;
  volume: number | null;
  timestamp: number;
  updateCount: number;
  lastValidUpdate: number;
  confidence: number; // 0-1 score based on data quality
  sourceTimestamp?: number | null;
}

export interface QuoteCacheEvent {
  quote: CachedQuote;
  raw: QuoteData;
}

type QuoteCacheListener = (event: QuoteCacheEvent) => void;

interface QuoteValidation {
  isValid: boolean;
  reason?: string;
  confidence: number;
}

export class QuotesCacheService {
  // In-memory cache
  private cache = new Map<string, CachedQuote>();
  private persistedHydrationPromise: Promise<void> | null = null;

  // History for validation (keep last N values)
  private history = new Map<string, QuoteData[]>();
  private maxHistorySize = 20;

  private quoteListeners = new Set<QuoteCacheListener>();

  // Validation thresholds
  private readonly CONFIDENCE_THRESHOLD = 0.5;
  private readonly MAX_SPREAD_PERCENTAGE = 10; // Max 10% spread
  private readonly STALE_DATA_MINUTES = 2;
  private readonly MIN_PRICE_THRESHOLD = 0.01;

  // Storage key for localStorage
  private readonly STORAGE_KEY = "tastymaestro_quotes_cache";

  constructor() {
    // Initialize from localStorage if available
    this.loadFromStorage();
    this.rebuildCacheIndex();

    if (typeof window !== "undefined") {
      // Set up periodic cleanup
      setInterval(() => this.cleanupStaleData(), 30000); // Every 30 seconds

      // Set up periodic storage save
      setInterval(() => this.saveToStorage(), 60000); // Every minute
    }
  }

  addQuoteListener(listener: QuoteCacheListener): () => void {
    this.quoteListeners.add(listener);
    return () => {
      this.quoteListeners.delete(listener);
    };
  }

  private notifyQuoteListeners(event: QuoteCacheEvent): void {
    if (this.quoteListeners.size === 0) {
      return;
    }

    for (const listener of this.quoteListeners) {
      try {
        listener(event);
      } catch (error) {
        console.error("[QuotesCacheService] Quote listener error", error);
      }
    }
  }

  /**
   * Process incoming quote data
   */
  processQuoteUpdate(quoteData: QuoteData): void {
    // Debug SPY quotes
    // if (quoteData.eventSymbol.includes("SPY")) {
    //   console.log("[QuotesCacheService] Processing SPY quote:", {
    //     eventSymbol: quoteData.eventSymbol,
    //     bidPrice: quoteData.bidPrice,
    //     askPrice: quoteData.askPrice,
    //     timestamp: quoteData.timestamp,
    //   });
    // }

    const validation = this.validateQuote(quoteData);

    // Debug validation result for July 25th
    // if (quoteData.eventSymbol.includes("250725")) {
    //   console.log("[QuotesCacheService] July 25th validation:", {
    //     eventSymbol: quoteData.eventSymbol,
    //     isValid: validation.isValid,
    //     reason: validation.reason,
    //     confidence: validation.confidence,
    //   });
    // }

    // Processing quote update

    // Check if this is just a zero-price update that shouldn't overwrite good data
    const existing = this.getQuote(quoteData.eventSymbol);
    if (
      existing &&
      existing.bidPrice &&
      existing.askPrice &&
      existing.bidPrice > 0 &&
      existing.askPrice > 0
    ) {
      // We have good existing data
      if (
        (quoteData.bidPrice === 0 || quoteData.askPrice === 0) &&
        !quoteData.lastPrice
      ) {
        // Debug: Skip zero price update
        if (quoteData.eventSymbol.includes("250725")) {
          console.log(
            "[QuotesCacheService] Skipping zero price update for July 25th:",
            {
              eventSymbol: quoteData.eventSymbol,
              existingBid: existing.bidPrice,
              existingAsk: existing.askPrice,
              newBid: quoteData.bidPrice,
              newAsk: quoteData.askPrice,
            },
          );
        }
        // Skip zero price updates that would clear good data
        return;
      }
    }

    if (!validation.isValid) {
      // Quote validation failed
      this.addToHistory(quoteData.eventSymbol, quoteData);

      // Store invalid quotes in history for analysis
      quotesDatabaseService.storeQuoteHistory(
        quoteData.eventSymbol,
        quoteData,
        false,
        validation.reason,
        validation.confidence,
      );

      return;
    }

    // Update cache with valid data
    this.updateCache(quoteData, validation.confidence);
  }

  /**
   * Validate quote data
   */
  private validateQuote(quoteData: QuoteData): QuoteValidation {
    // Check for any price data
    const hasBidAsk =
      quoteData.bidPrice !== undefined || quoteData.askPrice !== undefined;
    const hasLast = quoteData.lastPrice !== undefined;

    if (!hasBidAsk && !hasLast) {
      return { isValid: false, reason: "No price data", confidence: 0 };
    }

    // Validate bid/ask spread if both are present
    if (quoteData.bidPrice !== undefined && quoteData.askPrice !== undefined) {
      const bid = quoteData.bidPrice || 0;
      const ask = quoteData.askPrice || 0;

      // Check for invalid prices
      if (bid < 0 || ask < 0) {
        return { isValid: false, reason: "Negative prices", confidence: 0 };
      }

      // Check for crossed market
      if (bid > ask && bid > 0 && ask > 0) {
        return { isValid: false, reason: "Crossed market", confidence: 0 };
      }

      // Check spread percentage
      if (bid > 0 && ask > 0) {
        const midPrice = (bid + ask) / 2;
        const spread = ask - bid;
        const spreadPercentage = (spread / midPrice) * 100;

        if (spreadPercentage > this.MAX_SPREAD_PERCENTAGE) {
          return {
            isValid: false,
            reason: `Spread too wide: ${spreadPercentage.toFixed(2)}%`,
            confidence: 0.3,
          };
        }
      }
    }

    // Validate against history
    const historyValidation = this.validateAgainstHistory(quoteData);
    if (!historyValidation.isValid) {
      return historyValidation;
    }

    // Calculate confidence based on data completeness
    let confidence = 1.0;

    // Reduce confidence for partial data
    if (!hasBidAsk && hasLast) confidence *= 0.8;
    if (hasBidAsk && !hasLast) confidence *= 0.9;

    // Reduce confidence for zero prices but still process them
    // (we'll keep existing good data in updateCache)
    if (quoteData.bidPrice === 0 || quoteData.askPrice === 0) confidence *= 0.5;

    // Reduce confidence for very small prices (likely test data)
    if (quoteData.bidPrice && quoteData.bidPrice < this.MIN_PRICE_THRESHOLD)
      confidence *= 0.5;

    return { isValid: true, confidence };
  }

  /**
   * Validate against historical data
   */
  private validateAgainstHistory(quoteData: QuoteData): QuoteValidation {
    const symbolHistory = this.history.get(quoteData.eventSymbol);
    if (!symbolHistory || symbolHistory.length < 3) {
      return { isValid: true, confidence: 0.9 }; // Not enough history
    }

    // Get recent valid prices
    const recentPrices = symbolHistory
      .slice(-5)
      .map((q) => q.lastPrice || ((q.bidPrice || 0) + (q.askPrice || 0)) / 2)
      .filter((p) => p > 0);

    if (recentPrices.length === 0) {
      return { isValid: true, confidence: 0.8 };
    }

    const avgPrice =
      recentPrices.reduce((a, b) => a + b, 0) / recentPrices.length;
    const currentPrice =
      quoteData.lastPrice ||
      ((quoteData.bidPrice || 0) + (quoteData.askPrice || 0)) / 2;

    // Check for extreme price movements (>20% from average)
    if (currentPrice > 0 && avgPrice > 0) {
      const priceChange = Math.abs(currentPrice - avgPrice) / avgPrice;
      if (priceChange > 0.2) {
        return {
          isValid: false,
          reason: `Extreme price movement: ${(priceChange * 100).toFixed(1)}%`,
          confidence: 0.4,
        };
      }
    }

    return { isValid: true, confidence: 1.0 };
  }

  /**
   * Update cache with validated quote data
   */
  private updateCache(quoteData: QuoteData, confidence: number): void {
    const rawEventSymbol =
      typeof quoteData.eventSymbol === "string"
        ? quoteData.eventSymbol.trim()
        : "";
    if (!rawEventSymbol) {
      return;
    }

    const symbolVariants = deriveSymbolVariants(
      rawEventSymbol,
      symbolMappingService,
    );

    if (
      symbolVariants.canonicalStreamer &&
      symbolVariants.canonicalPosition
    ) {
      symbolMappingService.addWebSocketMapping(
        symbolVariants.canonicalStreamer,
        symbolVariants.canonicalPosition,
      );
    }

    const aliasKeys = buildAliasKeys(
      [
        rawEventSymbol,
        symbolVariants.trimmed,
        symbolVariants.mappingPosition,
        symbolVariants.canonicalStreamer,
        symbolVariants.canonicalPosition,
        symbolVariants.compactSymbol,
      ],
      { hasKey: this.cache.has.bind(this.cache) },
    );

    if (aliasKeys.length === 0) {
      aliasKeys.push(rawEventSymbol);
    }

    let existing: CachedQuote | null = null;
    for (const key of aliasKeys) {
      const candidate = this.cache.get(key);
      if (candidate) {
        existing = candidate;
        break;
      }
    }

    const primaryStreamerSymbol =
      symbolVariants.canonicalStreamer ??
      existing?.streamerSymbol ??
      symbolVariants.trimmed;

    const resolvedPositionSymbol =
      symbolVariants.canonicalPosition ??
      symbolVariants.mappingPosition ??
      existing?.symbol ??
      primaryStreamerSymbol;

    const hasNewBid =
      quoteData.bidPrice !== undefined && quoteData.bidPrice > 0;
    const hasNewAsk =
      quoteData.askPrice !== undefined && quoteData.askPrice > 0;
    const hasNewLast =
      quoteData.lastPrice !== undefined && quoteData.lastPrice > 0;
    const hasNewVolume =
      quoteData.volume !== undefined && quoteData.volume > 0;

    let bidPrice = existing?.bidPrice ?? null;
    let askPrice = existing?.askPrice ?? null;
    let lastPrice = existing?.lastPrice ?? null;

    if (hasNewBid && quoteData.bidPrice !== undefined) {
      bidPrice = quoteData.bidPrice;
    }
    if (hasNewAsk && quoteData.askPrice !== undefined) {
      askPrice = quoteData.askPrice;
    }
    if (hasNewLast && quoteData.lastPrice !== undefined) {
      lastPrice = quoteData.lastPrice;
    }

    let midPrice: number | null = null;
    let spread: number | null = null;
    let spreadPercentage: number | null = null;

    if (
      bidPrice !== null &&
      askPrice !== null &&
      bidPrice > 0 &&
      askPrice > 0
    ) {
      midPrice = (bidPrice + askPrice) / 2;
      spread = askPrice - bidPrice;
      spreadPercentage = (spread / midPrice) * 100;
    }

    let volume = existing?.volume ?? null;
    if (hasNewVolume && quoteData.volume !== undefined) {
      volume = quoteData.volume;
    }

    const providedFreshData =
      hasNewBid || hasNewAsk || hasNewLast || hasNewVolume;

    const now = Date.now();
    const observedTimestamp =
      typeof quoteData.timestamp === "number" &&
      Number.isFinite(quoteData.timestamp)
        ? quoteData.timestamp
        : now;
    const sourceTimestamp =
      typeof quoteData.sourceTimestamp === "number" &&
      Number.isFinite(quoteData.sourceTimestamp)
        ? quoteData.sourceTimestamp
        : observedTimestamp;

    const aliasKeySet = new Set(aliasKeys);

    const cachedQuote: CachedQuote = {
      symbol: resolvedPositionSymbol,
      streamerSymbol: primaryStreamerSymbol,
      bidPrice,
      askPrice,
      bidSize: quoteData.bidSize ?? existing?.bidSize ?? null,
      askSize: quoteData.askSize ?? existing?.askSize ?? null,
      lastPrice,
      midPrice,
      spread,
      spreadPercentage,
      volume,
      timestamp: providedFreshData
        ? observedTimestamp
        : existing?.timestamp ?? observedTimestamp,
      updateCount: (existing?.updateCount || 0) + 1,
      lastValidUpdate: providedFreshData
        ? observedTimestamp
        : existing?.lastValidUpdate ??
          existing?.timestamp ??
          observedTimestamp,
      confidence: providedFreshData
        ? Math.max(confidence, existing?.confidence || 0) * 0.95
        : existing?.confidence ?? confidence,
      sourceTimestamp,
    };

    if (
      primaryStreamerSymbol.includes("PYPL260320C") ||
      resolvedPositionSymbol.includes("PYPL260320C")
    ) {
      console.log('[QuotesCache] update for PYPL260320C', {
        eventSymbol: quoteData.eventSymbol,
        streamerSymbol: primaryStreamerSymbol,
        cachedSymbol: resolvedPositionSymbol,
        bidPrice,
        askPrice,
        lastPrice,
        midPrice,
        providedFreshData,
        raw: quoteData,
      });
    }

    const noPriceChange =
      existing &&
      bidPrice === existing.bidPrice &&
      askPrice === existing.askPrice &&
      lastPrice === existing.lastPrice &&
      cachedQuote.midPrice === existing.midPrice &&
      cachedQuote.spread === existing.spread &&
      cachedQuote.volume === existing.volume;

    if (noPriceChange && !providedFreshData) {
      return;
    }

    aliasKeys.forEach((key) => {
      if (key) {
        this.cache.set(key, cachedQuote);
      }
    });

    if (existing && existing !== cachedQuote) {
      removeLegacyAliases(this.cache, existing, aliasKeySet);
    }

    this.addToHistory(primaryStreamerSymbol, quoteData);

    quotesDatabaseService.storeQuoteCache(
      primaryStreamerSymbol,
      cachedQuote,
    );

    const emittedQuote: QuoteData = {
      eventType: "QUOTE",
      eventSymbol: primaryStreamerSymbol,
      bidPrice: cachedQuote.bidPrice ?? undefined,
      askPrice: cachedQuote.askPrice ?? undefined,
      bidSize: cachedQuote.bidSize ?? undefined,
      askSize: cachedQuote.askSize ?? undefined,
      lastPrice: cachedQuote.lastPrice ?? undefined,
      volume: cachedQuote.volume ?? undefined,
      timestamp: cachedQuote.timestamp,
      sourceTimestamp: cachedQuote.sourceTimestamp ?? undefined,
    };

    this.notifyQuoteListeners({ quote: cachedQuote, raw: emittedQuote });
  }

  private rebuildCacheIndex(): void {
    if (this.cache.size === 0) {
      return;
    }

    const uniqueQuotes = Array.from(new Set(this.cache.values()));
    this.cache.clear();

    uniqueQuotes.forEach((quote) => {
      if (!quote || typeof quote.streamerSymbol !== "string") {
        return;
      }

      const variants = deriveSymbolVariants(
        quote.streamerSymbol,
        symbolMappingService,
        typeof quote.symbol === "string" ? quote.symbol : null,
      );

      const aliasKeys = buildAliasKeys(
        [
          quote.streamerSymbol,
          quote.symbol,
          variants.mappingPosition,
          variants.canonicalStreamer,
          variants.canonicalPosition,
          variants.compactSymbol,
        ],
        { hasKey: this.cache.has.bind(this.cache) },
      );

      if (aliasKeys.length === 0) {
        aliasKeys.push(quote.streamerSymbol.trim());
      }

      aliasKeys.forEach((key) => {
        if (key) {
          this.cache.set(key, quote);
        }
      });
    });
  }

  /**
   * Add quote to history
   */
  private addToHistory(symbol: string, quoteData: QuoteData): void {
    const symbolHistory = this.history.get(symbol) || [];
    symbolHistory.push(quoteData);

    // Keep only recent history
    if (symbolHistory.length > this.maxHistorySize) {
      symbolHistory.splice(0, symbolHistory.length - this.maxHistorySize);
    }

    this.history.set(symbol, symbolHistory);
  }

  /**
   * Get cached quote for a symbol
   */
  getQuote(symbol: string): CachedQuote | null {
    let quote = this.cache.get(symbol) ?? null;

    const trimmed = typeof symbol === "string" ? symbol.trim() : "";
    if (!quote && trimmed) {
      quote = this.cache.get(trimmed) ?? null;
    }

    const normalized = trimmed.toUpperCase();
    if (!quote && normalized) {
      quote = this.cache.get(normalized) ?? null;
    }

    if (!quote && trimmed) {
      const streamer =
        symbolMappingService.getStreamerSymbol(trimmed) ||
        symbolMappingService.getStreamerSymbol(normalized) ||
        (trimmed.startsWith(".") ? trimmed : null);
      if (streamer) {
        const streamerTrimmed = streamer.trim();
        quote =
          this.cache.get(streamerTrimmed) ||
          this.cache.get(streamerTrimmed.toUpperCase()) ||
          null;
      }
    }

    if (!quote && trimmed) {
      const position =
        symbolMappingService.getPositionSymbol(trimmed) ||
        symbolMappingService.getPositionSymbol(normalized);
      if (position) {
        const positionTrimmed = position.trim();
        quote =
          this.cache.get(positionTrimmed) ||
          this.cache.get(positionTrimmed.toUpperCase()) ||
          null;
      }
    }

    if (quote) {
      const ageMinutes = (Date.now() - quote.timestamp) / 60000;
      if (ageMinutes > this.STALE_DATA_MINUTES) {
        quote.confidence *= Math.max(0.5, 1 - ageMinutes / 10);
      }
    }

    return quote;
  }

  /**
   * Get all cached quotes
   */
  getBestPrice(symbol: string): number | null {
    const quote = this.getQuote(symbol);
    if (!quote) return null;

    // Priority: mid price > last price > bid price
    return quote.midPrice || quote.lastPrice || quote.bidPrice || null;
  }

  /**
   * Hydrate the browser cache from the persisted database cache.
   * LocalStorage can lag behind the server-side quote cache, especially after
   * another tab/session refreshes quotes. Only newer persisted rows replace
   * existing in-memory rows.
   */
  hydrateFromPersistedCache(force = false): Promise<void> {
    if (typeof window === "undefined") {
      return Promise.resolve();
    }

    if (this.persistedHydrationPromise && !force) {
      return this.persistedHydrationPromise;
    }

    this.persistedHydrationPromise = (async () => {
      const persistedQuotes = await quotesDatabaseService.getCachedQuotes();
      if (persistedQuotes.size === 0) {
        this.persistedHydrationPromise = null;
        return;
      }

      persistedQuotes.forEach((quote, symbol) => {
        const existing = this.cache.get(symbol);
        if (!existing || quote.timestamp >= existing.timestamp) {
          this.cache.set(symbol, quote);
        }
      });

      this.rebuildCacheIndex();
      this.saveToStorage();
    })().catch((error) => {
      this.persistedHydrationPromise = null;
      console.warn("[QuotesCacheService] Failed to hydrate persisted cache:", error);
    });

    return this.persistedHydrationPromise;
  }

  /**
   * Clean up stale data
   */
  private cleanupStaleData(): void {
    const now = Date.now();
    const staleThreshold = this.STALE_DATA_MINUTES * 60 * 1000;

    for (const [, quote] of this.cache.entries()) {
      if (now - quote.timestamp > staleThreshold) {
        // Don't delete, just mark as low confidence
        quote.confidence = Math.min(quote.confidence, 0.3);
      }
    }
  }

  /**
   * Load from localStorage
   */
  private loadFromStorage(): void {
    if (typeof window === "undefined") return;

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.cache) {
          this.cache = new Map(Object.entries(data.cache));
          // Loaded quotes from storage
        }
      }
    } catch {
      // Failed to load quotes from storage
    }
  }

  /**
   * Save to localStorage
   */
  private saveToStorage(): void {
    if (typeof window === "undefined") return;

    try {
      const data = {
        cache: Object.fromEntries(this.cache),
        timestamp: Date.now(),
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Failed to save quotes to storage
    }
  }

  /**
   * Clear all cached data
   */
  clear(): void {
    this.cache.clear();
    this.history.clear();
    this.saveToStorage();
    // Quotes cache cleared
  }
  
  /**
   * Get all cached quotes (for aggregator)
   */
  getAllQuotes(): Map<string, CachedQuote> {
    return new Map(this.cache);
  }
}

// Create singleton instance
const globalAny = globalThis as typeof globalThis & {
  __tastymaestroQuotesCache?: QuotesCacheService;
};

if (!globalAny.__tastymaestroQuotesCache) {
  globalAny.__tastymaestroQuotesCache = new QuotesCacheService();
}

export const quotesCacheService = globalAny.__tastymaestroQuotesCache;

type DebugQuotesCache = {
  getCache: () => Array<[string, CachedQuote]>;
  getCacheSize: () => number;
  getSPYQuotes: () => Array<[string, CachedQuote]>;
  getQuote: (symbol: string) => CachedQuote | null;
  service: QuotesCacheService;
  getJuly25Quotes: () => Array<[string, CachedQuote]>;
  checkSubscriptions: () => {
    total: number;
    spy: number;
    july25: number;
    july21: number;
  };
};

// Debug helper for browser console
if (typeof window !== "undefined") {
  const globalWindow = window as typeof window & {
    debugQuotesCache?: DebugQuotesCache;
  };

  globalWindow.debugQuotesCache = {
    getCache: () => Array.from(quotesCacheService.getAllQuotes().entries()),
    getCacheSize: () => quotesCacheService.getAllQuotes().size,
    getSPYQuotes: () =>
      Array.from(quotesCacheService.getAllQuotes().entries()).filter(([k]) =>
        k.includes("SPY"),
      ),
    getQuote: (symbol: string) => quotesCacheService.getQuote(symbol),
    service: quotesCacheService,
    getJuly25Quotes: () => {
      const all = Array.from(quotesCacheService.getAllQuotes().entries());
      return all.filter(([k]) => k.includes("250725"));
    },
    checkSubscriptions: () => {
      console.log("=== Quote Cache Debug ===");
      console.log("Total cache size:", quotesCacheService.getAllQuotes().size);

      const spyQuotes = Array.from(
        quotesCacheService.getAllQuotes().entries(),
      ).filter(([k]) => k.includes("SPY"));
      console.log("SPY quotes:", spyQuotes.length);

      const july25 = spyQuotes.filter(([k]) => k.includes("250725"));
      console.log("July 25th SPY options:", july25.length);

      if (july25.length > 0) {
        console.log("Sample July 25th quotes:", july25.slice(0, 3));
      }

      const july21 = spyQuotes.filter(([k]) => k.includes("250721"));
      console.log("July 21st SPY options:", july21.length);

      return {
        total: quotesCacheService.getAllQuotes().size,
        spy: spyQuotes.length,
        july25: july25.length,
        july21: july21.length,
      };
    },
  };
}
